import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mic } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function InterviewTipsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login?redirect=/interview' + (searchParams.toString() ? '?' + searchParams.toString() : ''));
    }
  }, [user, loading, navigate, searchParams]);

  const handleBegin = async () => {
    setIsNavigating(true);
    setError(null);

    try {
      const sessionId = searchParams.get('sessionId');
      const assistantId = searchParams.get('assistantId');
      const jobId = searchParams.get('jobId');

      if (sessionId) {
        const assistantIdParam = assistantId ? `&assistantId=${assistantId}` : '';
        navigate(`/interview/session?sessionId=${sessionId}${assistantIdParam}`);
      } else if (jobId) {
        console.log('[INTERVIEW] Starting session for jobId:', jobId);

        // Direct Supabase implementation
        const { supabase } = await import('@/lib/supabase-client');
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          navigate('/login?redirect=/interview' + (jobId ? `?jobId=${jobId}` : ''));
          return;
        }

        // Fetch job and config
        const { data: job, error: jobError } = await supabase
          .from('job_postings')
          .select('*, interview_configurations(*)')
          .eq('id', jobId)
          .single();

        if (jobError || !job) {
          throw new Error('Job not found');
        }

        // Create interview session
        const { data: session, error: sessionError } = await supabase
          .from('interview_sessions')
          .insert({
            job_posting_id: jobId,
            interview_config_id: job.interview_config_id,
            user_id: user.id,
            candidate_name: user.email?.split('@')[0] || 'Candidate',
            candidate_email: user.email || '',
            status: 'started',
            started_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (sessionError || !session) {
          throw new Error('Failed to create session');
        }

        console.log('[INTERVIEW] Session started:', session.id);

        // Store interview data
        const config = job.interview_configurations;
        if (config?.role_statement) {
          sessionStorage.setItem('interviewSystemPrompt', config.role_statement);
        }

        // Add voiceGender at top level for easy access
        const jobDataWithVoiceGender = {
          ...job,
          voiceGender: config?.voice_gender || 'male'
        };
        sessionStorage.setItem('interviewJobInfo', JSON.stringify(jobDataWithVoiceGender));

        if (config?.default_questions) {
          sessionStorage.setItem('interviewQuestions', JSON.stringify(config.default_questions));
        }

        const assistantIdParam = config?.assistant_id ? `&assistantId=${config.assistant_id}` : '';
        navigate(`/interview/session?sessionId=${session.id}${assistantIdParam}`);
      } else {
        navigate('/interview/session');
      }
    } catch (err: any) {
      console.error('[INTERVIEW] Error starting session:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.message || 'Failed to start interview. Please try again.');
      }
      setIsNavigating(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 space-y-8 relative">

        {/* Subtle decorative top accent */}
        <div className="absolute -top-3 left-8 right-8 h-2 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"></div>

        {/* Loading icon at top */}
        {isNavigating && (
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Mic className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
              <svg className="absolute inset-0 w-16 h-16 -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  className="text-emerald-200"
                  strokeDasharray="176"
                  strokeDashoffset="44"
                  strokeLinecap="round"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="176;0"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-bold shadow-md">
            IT
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-emerald-600 font-semibold">
              Interview Trainer
            </p>
            <p className="text-sm text-slate-500">Getting things ready…</p>
          </div>

          <div className="flex ml-auto items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '0.15s' }}></span>
            <span className="h-2 w-2 rounded-full bg-emerald-300 animate-pulse" style={{ animationDelay: '0.3s' }}></span>
          </div>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-slate-900">
            Before we begin your practice interview
          </h1>
          <p className="text-sm text-slate-600">
            A quick overview to make sure you feel comfortable and confident.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">

          {/* Not judged */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-emerald-700 mb-2">💚 What we don&apos;t judge</p>
            <ul className="space-y-2 text-sm text-emerald-900">
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Your appearance, hoodie / T-shirt, hairstyle, or accent
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Your race, gender, age, disability, religion, or identity
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Your background or personal characteristics
              </li>
            </ul>
          </div>

          {/* Judged */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-slate-800 mb-2">🟢 What we do look at</p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Staying present on camera & keeping your face visible (hood down)
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Speaking clearly & staying on-topic
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Minimising big distractions (full meals, leaving frame, loud noise)
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Showing standard interview focus & engagement
              </li>
            </ul>
            <p className="text-xs text-slate-500 mt-2">
              These aren&apos;t strict rules — just coaching to help you shine in real interviews.
            </p>
          </div>

          {/* Moderation */}
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-rose-700 mb-2">❌ What isn&apos;t allowed</p>
            <ul className="space-y-2 text-sm text-rose-900">
              <li className="flex gap-2">
                <span className="text-rose-500">•</span>
                Nudity or sexual content
              </li>
              <li className="flex gap-2">
                <span className="text-rose-500">•</span>
                Hate symbols or abusive gestures
              </li>
              <li className="flex gap-2">
                <span className="text-rose-500">•</span>
                Deliberately inappropriate or unsafe behaviour
              </li>
            </ul>
            <p className="text-xs text-rose-600 mt-2">
              If detected, your interview will pause and you may receive a warning.
            </p>
          </div>

          {/* Privacy / recording */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-slate-800 mb-2">🔐 About your recording</p>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                Your interview video is <strong>not shared</strong> with recruiters or third parties unless you
                choose to share it.
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                The recording is used to analyse your interview and is <strong>not kept long-term</strong> unless
                you decide to save it in your account.
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                For behaviour analysis, we send a few still images (frames) to our AI provider. These are
                processed securely and <strong>not used to train AI models</strong>.
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-500">•</span>
                You can delete your recording and associated data at any time.
              </li>
            </ul>
          </div>

          {/* Final */}
          <p className="text-sm text-slate-700">
            This session is for <strong>practice and growth</strong>. Your score guides you — it doesn&apos;t define you.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="flex items-center justify-between pt-2">
          <button className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700">
            Privacy details
          </button>
          <button
            onClick={handleBegin}
            disabled={isNavigating}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2 text-sm font-medium text-white shadow-lg hover:bg-emerald-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isNavigating ? 'Starting interview...' : 'I understand — Start my interview'}
          </button>
        </div>
      </div>
    </div>
  );
}
